import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { Material, MaterialBackend } from "@/store/modules/materialModule";
import cloneDeep from "lodash/cloneDeep";

export type Materialgrupp = {
    id: number;
    namn: string;
    gruppnummer: string;
    streckkod: string;
    material: {
        antal: number;
        material: Material["id"];
        materialmaterialgrupp: number;
    }[];
};

export type MaterialgruppBackend = Omit<Materialgrupp, "material"> & {
    materialmaterialgrupp: {
        id: Materialgrupp["id"];
        antal: number;
        material_display: MaterialBackend;
    }[];
};

type State = BaseState & {
    materialgrupper: Map<Materialgrupp["id"], Materialgrupp>;
};

type Getters = {
    materialgruppById: (data: { id: Materialgrupp["id"] }) => Materialgrupp | null;
};

type Commit = (
    action: string,
    payload: unknown,
    options?: {
        root?: boolean;
    }
) => void;

const convertBackendData: (props: { data: MaterialgruppBackend }) => Materialgrupp = ({ data }) => {
    const { materialmaterialgrupp, ...materialgrupp } = data;

    return {
        ...materialgrupp,
        material: materialmaterialgrupp.map((item) => ({
            material: item.material_display.id,
            antal: item.antal,
            materialmaterialgrupp: item.id,
            material_display: item.material_display,
        })),
    };
};

const getDefaultState: () => State = () => {
    return {
        materialgrupper: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "materialgrupper";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        materialgrupper: (state: State) => [...state.materialgrupper.values()],
        materialgrupperSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter({
                ...props,
                state,
                collectionName: "materialgrupper",
            }),
        materialgruppById:
            (state: State) =>
            ({ id }: { id?: Materialgrupp["id"] }) => {
                if (!id) {
                    return null;
                }

                return state.materialgrupper.get(id);
            },
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addMaterialgrupper: (
            state: State,
            {
                offset,
                subsetName = "default",
                materialgrupper = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                materialgrupper: Materialgrupp[];
            }
        ) =>
            subsetSetter<State, Materialgrupp>({
                offset,
                subsetName,
                state,
                collectionName: "materialgrupper",
                items: materialgrupper,
            }),
        updateMaterialgrupper: (
            state: State,
            {
                materialgrupper,
                offset,
                subsetName,
            }: {
                materialgrupper: Materialgrupp[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Materialgrupp>({
                offset,
                state,
                collectionName: "materialgrupper",
                items: materialgrupper,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        getMaterialgruppById: async (
            {
                getters,
                commit,
            }: {
                getters: Getters;
                commit: Commit;
            },
            {
                id,
            }: {
                id: Materialgrupp["id"];
            }
        ) => {
            const foundMaterialgrupp = getters.materialgruppById({ id });

            if (foundMaterialgrupp || !id) {
                return foundMaterialgrupp;
            }

            try {
                const { data } = await klinikenApi.get<{
                    materialgrupp: MaterialgruppBackend;
                }>(`operation/materialgrupp/${id}`, getConfig({}));

                if (data.materialgrupp) {
                    const retrievedMaterialgrupp = convertBackendData({ data: data.materialgrupp });

                    commit("addMaterialgrupper", {
                        offset: 0,
                        materialgrupper: [retrievedMaterialgrupp],
                        subsetName: id,
                    });

                    return retrievedMaterialgrupp;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        /**
         * Hämta material från state eller från backend och stoppa in i state. Den skapar ett nytt subset om
         * inskickade filteringParams inte redan finns i state.
         */
        fetchMaterialgrupper: async (
            { commit, state }: BaseActionsProps<State>,
            {
                filterParams,
                limit = 20,
                page = 1,
            }: {
                limit: number;
                page: number;
                filterParams: {
                    [key: string]: string | number | boolean;
                };
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, MaterialgruppBackend>({
                    filterParams,
                    state,
                    limit,
                    page,
                    collectionName: "materialgrupper",
                    apiUrl: "operation/materialgrupp/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                const materialMap = new Map<Material["id"], Material>();
                const allMaterialgrupper = results.map((materialgrupp) => {
                    materialgrupp.materialmaterialgrupp.forEach((singleMaterial) => {
                        materialMap.set(singleMaterial.material_display.id, singleMaterial.material_display);
                    });

                    return convertBackendData({ data: materialgrupp });
                });

                commit<{
                    offset: number;
                    materialgrupper: Materialgrupp[];
                }>("addMaterialgrupper", {
                    offset: offset as number,
                    materialgrupper: allMaterialgrupper,
                });

                commit<{
                    offset: number;
                    subsetName: keyof Subsets;
                    material: Material[];
                }>(
                    "material/addMaterial",
                    {
                        offset: offset as number,
                        subsetName: "materialgrupper" as keyof Subsets,
                        material: [...materialMap.values()],
                    },
                    { root: true }
                );

                return {
                    subsetName,
                    total,
                    nextPage,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createMaterialgrupp: async (
            { commit, state }: BaseActionsProps<State>,
            {
                materialgrupp,
            }: {
                materialgrupp: MaterialgruppBackend;
            }
        ) => {
            try {
                const response = await klinikenApi.post<MaterialgruppBackend>(
                    "operation/materialgrupp/",
                    materialgrupp,
                    getConfig({})
                );

                commit("addMaterialgrupper", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    materialgrupper: [convertBackendData({ data: response.data })],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateMaterialgrupp: async (
            { commit, state }: BaseActionsProps<State>,
            {
                materialgrupp,
            }: {
                materialgrupp: MaterialgruppBackend;
            }
        ) => {
            try {
                const response = await klinikenApi.put<MaterialgruppBackend>(
                    `operation/materialgrupp/${materialgrupp.id}/`,
                    materialgrupp,
                    getConfig({})
                );

                commit("updateMaterialgrupper", {
                    subsetName: "newlyUpdated",
                    materialgrupper: [convertBackendData({ data: response.data })],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
